import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Custom Hook to track user activity status.
 * @param {number} timeoutInSeconds - The time (in seconds) of inactivity to wait before returning 'false'.
 * @returns {boolean} isActive - Returns TRUE if the user is currently active, and FALSE if inactive/expired.
 */
const useActivityStatus = (timeoutInSeconds) => {
  // State to track the activity status. Initial state is active (true).
  const [isActive, setIsActive] = useState(true);
  const timeoutRef = useRef(null);
  
  // Events to monitor for activity
  const events = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

  // Function to reset the timer and set state to active (true)
  const resetTimer = useCallback(() => {
    // 1. Set the user to ACTIVE
    setIsActive(true);

    // 2. Clear the existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // 3. Set a new timer
    timeoutRef.current = setTimeout(() => {
      // 4. When the timer expires, set the user to INACTIVE (false)
      setIsActive(false);
    }, timeoutInSeconds * 1000); // Convert seconds to milliseconds
  }, [timeoutInSeconds]);

  // useEffect to register and clean up listeners
  useEffect(() => {
    // Start the initial timer
    resetTimer();

    // Attach listeners to reset the timer on any user activity
    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    // Cleanup: Remove listeners and clear the timer when the component unmounts
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer]); // Dependency array: Re-run only if resetTimer changes

  // Return the activity status
  return isActive; 
};

export default useActivityStatus;